import React, { ReactNode } from 'react'

import { Color } from 'themes'
import { Severity } from 'utils/types'

import Avatar from 'components_new/atoms/Avatar'
import Badge, { BadgeProps } from 'components_new/atoms/Badge'
import Icon, { IconName } from 'components_new/atoms/Icon'

import GroupAvatar from 'components_new/molecules/GroupAvatar'
import UserAvatar from 'components_new/molecules/UserAvatar'

import { DASHBOARD_COLOR, REPORT_COLOR } from 'utils/colors'

type UserVariant = 'company' | 'customer' | 'homepal' | 'role' | 'user'
type IconVariant =
  | 'analysis'
  | 'analysis_type'
  | 'comment'
  | 'dashboard'
  | 'entity'
  | 'file'
  | 'folder'
  | 'key_metric'
  | 'metric'
  | 'page'
  | 'report'
  | 'slice'
  | 'tab'
  | 'task'
  | 'tasks'

const ICON_NAMES: Record<
  IconVariant,
  {
    color?: Color
    iconName: IconName
  }
> = {
  // resources
  dashboard: {
    color: DASHBOARD_COLOR,
    iconName: 'Dashboard'
  },
  report: {
    color: REPORT_COLOR,
    iconName: 'Report'
  },
  // core
  entity: {
    iconName: 'Entity'
  },
  key_metric: {
    iconName: 'KeyMetric'
  },
  metric: { iconName: 'Metric' },
  // other
  analysis: {
    iconName: 'Analysis'
  },
  analysis_type: {
    iconName: 'AnalysisType'
  },
  comment: {
    iconName: 'Comment'
  },
  file: {
    iconName: 'File'
  },
  folder: {
    iconName: 'Folder'
  },
  page: { iconName: 'Page' },
  slice: { iconName: 'Slice' },
  tab: { iconName: 'Tab' },
  task: {
    iconName: 'Task'
  },
  // pages
  tasks: { iconName: 'Tasks' }
}

const isUserVariant = (v: GenericAvatarProps['variant']): v is UserVariant =>
  v === 'company' ||
  v === 'customer' ||
  v === 'homepal' ||
  v === 'role' ||
  v === 'user'

export interface GenericAvatarProps {
  badge?: {
    active: boolean
    color?: BadgeProps['color']
  }
  body?: string
  className?: string
  color?: Color | Severity
  component?: 'avatar'
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'inherit'
  src?: string
  sx?: object
  title?: string
  /** Optional when `src` is set — the image then stands on its own. */
  variant?: 'group' | UserVariant | IconVariant
}

const GenericAvatar = React.forwardRef<HTMLDivElement, GenericAvatarProps>(
  (props, ref) => {
    const {
      badge,
      body,
      className,
      color,
      component,
      size,
      src,
      sx,
      title,
      variant
    } = props

    if (variant === 'group')
      return (
        <GroupAvatar
          className={className}
          color={color}
          ref={ref}
          size={size === 'inherit' ? undefined : size} // $TODO: fix later.
          sx={sx}
          title={title}
        />
      )

    if (isUserVariant(variant))
      return (
        <UserAvatar
          className={className}
          email={body}
          name={title}
          ref={ref}
          size={size === 'inherit' ? undefined : size} // $TODO: fix later.
          src={src}
          sx={sx}
          variant={variant}
        />
      )

    /**
     * A logotype for the thing itself — a connected system, an integration.
     * The variant's icon is the fallback when the image fails to load, and
     * MUI renders the children for us in that case.
     */
    if (src)
      return (
        <Avatar
          className={className}
          ref={ref}
          size={
            size === 'inherit' ? undefined : size === 'xsmall' ? 'small' : size
          }
          src={src}
          sx={sx}
          variant="rounded"
        >
          {variant ? (
            <Icon
              color={ICON_NAMES[variant].color ?? 'disabled'}
              name={ICON_NAMES[variant].iconName}
            />
          ) : null}
        </Avatar>
      )

    if (!variant) return null

    return (
      <IconWrapper {...props}>
        <Badge
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          color={badge?.color ?? 'primary'}
          invisible={!badge?.active}
          overlap="circular"
          slotProps={{
            badge: {
              sx: {
                bottom: '20%',
                right: '45%'
              }
            }
          }}
          variant="dot"
        >
          <Icon
            color={ICON_NAMES[variant].color ?? 'disabled'}
            fontSize={size === 'xsmall' ? 'small' : size}
            name={ICON_NAMES[variant].iconName}
            ref={component !== 'avatar' ? ref : undefined}
            sx={component !== 'avatar' ? sx : undefined}
          />
        </Badge>
      </IconWrapper>
    )
  }
)

GenericAvatar.displayName = 'GenericAvatar'

export default GenericAvatar

const IconWrapper = React.forwardRef<
  HTMLDivElement,
  GenericAvatarProps & { children: ReactNode }
>((props, ref) => {
  const { children, component, size, sx } = props

  if (component !== 'avatar') return <>{children}</>

  return (
    <Avatar
      ref={ref}
      size={size === 'inherit' ? undefined : size === 'xsmall' ? 'small' : size}
      sx={sx}
      variant="rounded"
    >
      {children}
    </Avatar>
  )
})
